import { catalog, me, type StorefrontScope } from '../api/endpoints.ts';
import { ApiError } from '../api/client.ts';
import type { Category, HomeFeed, RecentOrder, StockEvent, StorefrontItem } from '../api/types.ts';
import { createStore } from '../core/store.ts';
import type { Lang } from '../i18n/index.ts';
import { applyStockTo } from './stores.ts';

/**
 * Главная: категории, баннеры, прошлые заказы, покупки, подборки и автоматические ленты.
 * Всё грузится параллельно; данные общие с поиском: до запроса он показывает покупки и подборки.
 */
export type HomeState = {
  feed: HomeFeed | null;
  categories: Category[];
  recentOrders: RecentOrder[];
  purchases: StorefrontItem[];
  /** Товары склада, когда у главной нет ни подборок, ни автоматических лент. */
  fallback: StorefrontItem[];
  error: ApiError | null;
  isLoading: boolean;
};

export const homeStore = createStore<HomeState>({
  feed: null,
  categories: [],
  recentOrders: [],
  purchases: [],
  fallback: [],
  error: null,
  isLoading: false,
});

const patch = (next: Partial<HomeState>) => homeStore.set((state) => ({ ...state, ...next }));

const bySortOrder = (a: Category, b: Category) => a.sortOrder - b.sortOrder;

/** Разделы каталога верхнего уровня в порядке админки. */
export function sectionsOf(categories: Category[]): Category[] {
  return categories.filter((category) => category.parentId === null).sort(bySortOrder);
}

export function childrenOf(categories: Category[], section: Category): Category[] {
  return categories.filter((category) => category.parentId === section.id).sort(bySortOrder);
}

/** Соседи категории внутри раздела: чипы быстрого перехода. */
export function siblingsOf(categories: Category[], category: Category): Category[] {
  if (!category.parentId) return [];
  return categories.filter((item) => item.parentId === category.parentId).sort(bySortOrder);
}

/** Индекс раздела категории: по нему выбирается пастель плитки, та же, что у раздела в каталоге. */
export function sectionIndexOf(categories: Category[], category: Category): number {
  const index = sectionsOf(categories).findIndex((section) => section.id === (category.parentId ?? category.id));
  return index < 0 ? 0 : index;
}

/** Плитки ленты главной: подкатегории, а без разделов — все категории. */
export function homeTiles(categories: Category[]): Category[] {
  const parents = new Set(categories.map((category) => category.parentId));
  return categories.filter((category) => !parents.has(category.id)).sort(bySortOrder);
}

let generation = 0;

const FALLBACK_LIMIT = 60;

function hasFeedItems(feed: HomeFeed): boolean {
  return (
    feed.collections.some((collection) => collection.items.length > 0) ||
    feed.deals.length > 0 ||
    feed.newItems.length > 0 ||
    feed.mostOrdered.length > 0
  );
}

/**
 * `signedIn` решает, читать ли личные ленты («Заказать снова», покупки): у гостя их нет,
 * а ручки без сессии закрыты.
 */
export async function loadHome(scope: StorefrontScope, lang: Lang, signedIn: boolean): Promise<void> {
  const current = ++generation;
  patch({ isLoading: homeStore.get().feed === null });

  const [feed, categories, recent, purchases] = await Promise.all([
    catalog.home(scope, lang).catch(() => null),
    catalog.categories(lang).catch(() => null),
    signedIn ? me.recentOrders(5).catch(() => null) : null,
    signedIn ? catalog.purchases(scope, lang, 12).catch(() => null) : null,
  ]);
  // Ответ устаревшего запроса (сменили адрес или язык) не должен перекрыть свежий.
  if (current !== generation) return;

  const fallback = feed && !hasFeedItems(feed) ? await catalog.storefront(scope, lang, FALLBACK_LIMIT).catch(() => null) : null;
  if (current !== generation) return;

  const next: Partial<HomeState> = { isLoading: false };
  if (feed) {
    next.feed = feed;
    next.fallback = fallback?.items ?? [];
    next.error = null;
  } else if (homeStore.get().feed === null) {
    next.error = ApiError.offline();
  }
  if (categories) next.categories = categories.items;
  if (!signedIn) {
    next.recentOrders = [];
    next.purchases = [];
  }
  if (recent) next.recentOrders = recent.items;
  if (purchases) next.purchases = purchases.items;
  patch(next);
}

/** Остаток из ленты событий: закончившийся товар уходит из всех лент главной. */
export function applyStockToHome(event: StockEvent): void {
  const state = homeStore.get();
  const purchases = applyStockTo(state.purchases, event);
  const fallback = applyStockTo(state.fallback, event);
  let feed = state.feed;
  if (feed) {
    const collections = feed.collections.map((collection) => {
      const items = applyStockTo(collection.items, event);
      return items === collection.items ? collection : { ...collection, items };
    });
    const deals = applyStockTo(feed.deals, event);
    const newItems = applyStockTo(feed.newItems, event);
    const mostOrdered = applyStockTo(feed.mostOrdered, event);
    const touched =
      deals !== feed.deals ||
      newItems !== feed.newItems ||
      mostOrdered !== feed.mostOrdered ||
      collections.some((collection, index) => collection !== feed?.collections[index]);
    if (touched) feed = { ...feed, collections, deals, newItems, mostOrdered };
  }
  // Чужое событие не должно перестраивать ленты и перерисовывать экран.
  if (purchases !== state.purchases || fallback !== state.fallback || feed !== state.feed) patch({ purchases, fallback, feed });
}

/** Все товары, видимые на главной: по ним корзина сверяет цены и остатки. */
export function visibleHomeItems(state: HomeState): StorefrontItem[] {
  if (!state.feed) return state.purchases;
  return [
    ...state.feed.collections.flatMap((collection) => collection.items),
    ...state.feed.deals,
    ...state.feed.newItems,
    ...state.feed.mostOrdered,
    ...state.fallback,
    ...state.purchases,
  ];
}
